import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const TeamSection: React.FC = () => {
  const { t } = useTranslation();

  const team = [
    {
      id: 1,
      name: 'Ronny Amores',
      role: 'CEO',
      quote: t('team_section.member_1_quote'),
      img: '/founder_1.png'
    },
    {
      id: 2,
      name: 'Carlos Córdova',
      role: 'CTO',
      quote: t('team_section.member_2_quote'),
      img: '/founder_2.png'
    }
  ];

  return (
    <section className="team-section">
      <style>{`
        .team-section {
          padding: 4rem 0 8rem;
          background: var(--bg-dark);
          position: relative;
        }

        .team-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 5vw;
        }

        .team-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        /* Mismo lenguaje que "Por qué elegirnos" / "Prueba social" más abajo
           en esta misma página (AboutUs.tsx): eyebrow en pill, título con
           el cierre en degradado teal→purple (.grad-text es global, de
           index.css) y subtítulo del mismo tamaño/color apagado — antes
           esta sección tenía su propia tipografía "Apple" aislada (blanco a
           gris, mucho más grande) que no combinaba con el resto. */
        .team-eyebrow {
          display: inline-flex;
          align-items: center;
          font-family: var(--font-body);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--accent-teal);
          background: rgba(0, 229, 209, 0.08);
          border: 1px solid rgba(0, 229, 209, 0.28);
          padding: 0.35rem 0.85rem;
          border-radius: 9999px;
          margin-bottom: 1.2rem;
        }

        .apple-headline {
          font-family: var(--font-heading);
          font-size: clamp(2.2rem, 4.6vw, 3.4rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1.15;
          margin-bottom: 1rem;
          color: var(--text-primary);
        }

        .apple-subheadline {
          font-size: clamp(1.05rem, 1.8vw, 1.25rem);
          line-height: 1.6;
          color: var(--text-secondary);
          max-width: 700px;
          margin: 0 auto;
          font-weight: 400;
        }

        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
        }

        .team-card {
          position: relative;
          border-radius: 40px;
          overflow: hidden;
          aspect-ratio: 4/5;
          cursor: pointer;
        }

        .team-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.8s var(--ease-out-expo);
        }

        .team-card:hover .team-img {
          transform: scale(1.04);
        }

        .team-card-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.9) 100%);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 3rem;
          transition: background 0.5s ease;
        }

        .team-card:hover .team-card-overlay {
          background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.95) 100%);
        }

        .team-info {
          transform: translateY(20px);
          transition: transform 0.5s var(--ease-out-expo);
        }

        .team-card:hover .team-info {
          transform: translateY(0);
        }

        .team-name {
          font-family: var(--font-heading);
          font-size: 2.5rem;
          font-weight: 800;
          color: #fff;
          margin-bottom: 0.25rem;
        }

        .team-role {
          font-size: 1.1rem;
          color: var(--accent-teal);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 1.5rem;
        }

        .team-quote {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.8);
          font-style: italic;
          opacity: 0;
          transition: opacity 0.5s ease;
        }

        .team-card:hover .team-quote {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .team-card { aspect-ratio: 1; border-radius: 30px; }
          .team-card-overlay { padding: 2rem; }
          .team-name { font-size: 2rem; }
          .team-quote { opacity: 1; font-size: 1rem; }
          .team-info { transform: translateY(0); }
        }
      `}</style>
      
      <div className="team-container">
        <motion.div 
          className="team-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="team-eyebrow">{t('team_section.eyebrow')}</span>
          <h2 className="apple-headline">
            {t('team_section.headline_pre')}
            <span className="grad-text" style={{ fontWeight: 800 }}>{t('team_section.headline_grad')}</span>
          </h2>
          <p className="apple-subheadline">
            {t('team_section.subheadline')}
          </p>
        </motion.div>

        <div className="team-grid">
          {team.map((member, i) => (
            <motion.div 
              key={member.id}
              className="team-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: i * 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <img src={member.img} alt={member.name} className="team-img" />
              <div className="team-card-overlay">
                <div className="team-info">
                  <h3 className="team-name">{member.name}</h3>
                  <div className="team-role">{member.role}</div>
                  <p className="team-quote">{member.quote}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
